
import { supabase } from "@/integrations/supabase/client";
import { CharterSignatory } from '@/types/charter';
import { z } from 'zod';

const signatorySchema = z.object({
  firstName: z.string().trim().min(1, 'Le prénom est requis').max(100),
  lastName: z.string().trim().min(1, 'Le nom est requis').max(100),
  email: z.string().trim().toLowerCase().email('Adresse email invalide').max(255),
  phone: z
    .string()
    .trim()
    .max(30)
    .regex(/^[0-9+().\s-]*$/, 'Numéro de téléphone invalide')
    .optional()
    .or(z.literal('')),
}).passthrough();

/**
 * Enregistre un signataire de la charte républicaine et envoie un email de vérification
 */
export const saveCharterSignatory = async (signatory: Omit<CharterSignatory, 'id' | 'createdAt'> & { phone?: string }) => {
  console.log('[DEBUG saveCharterSignatory] Début de la fonction avec les données:', signatory);
  
  const parsed = signatorySchema.safeParse(signatory);
  if (!parsed.success) {
    console.error('[DEBUG saveCharterSignatory] Données invalides');
    throw new Error(parsed.error.issues[0]?.message || 'Données invalides');
  }
  const validSignatory = parsed.data;

  try {
    // Vérification d'email existant déléguée à l'RPC (sécurisée côté serveur)
    // On saute le check côté client pour éviter un SELECT bloqué par les RLS.

    // On n'effectue plus de comptage côté client (séquence auto-incrémentée côté DB)


    const signatoryId = crypto.randomUUID();
    const verificationToken = crypto.randomUUID();

    const insertData = {
      id: signatoryId,
      first_name: validSignatory.firstName,
      last_name: validSignatory.lastName,
      email: validSignatory.email,
      phone: validSignatory.phone || null,
      email_verified: false, // Par défaut, l'email n'est pas vérifié
      verification_token: verificationToken // Génère un token unique pour la vérification
    };
    
    console.log('[DEBUG saveCharterSignatory] Tentative d\'insertion dans Supabase avec les données:', insertData);

    const { error } = await supabase
      .from('charter_signatories')
      .insert([insertData]);

    console.log('[DEBUG saveCharterSignatory] Réponse de Supabase (returning minimal):', { error });

    if (error) {
      console.error('[DEBUG saveCharterSignatory] Erreur Supabase:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint
      });
      
      if (error.code === '23505') { // Code pour violation de contrainte unique
        throw new Error('Cette adresse email a déjà été utilisée pour signer la charte');
      }
      
      throw new Error(`Erreur lors de l'enregistrement : ${error.message}`);
    }
    
    console.log('[DEBUG saveCharterSignatory] Insertion réussie');
    
    // Envoi de l'email de vérification via la fonction Edge
    try {
      console.log('[DEBUG saveCharterSignatory] Tentative d\'envoi de l\'email de vérification');
      
      // Utiliser la fonction invoke pour appeler la fonction Edge de manière plus fiable
      const { data: emailResult, error: emailError } = await supabase.functions.invoke('send-verification-email', {
        body: {
          firstName: signatory.firstName,
          lastName: signatory.lastName,
          email: signatory.email,
          signatoryId: signatoryId,
          verificationToken: verificationToken,
        }
      });
      
      console.log('[DEBUG saveCharterSignatory] Réponse de la fonction d\'envoi d\'email de vérification:', emailResult);
      
      if (emailError) {
        console.error('[DEBUG saveCharterSignatory] Erreur lors de l\'appel de la fonction d\'envoi d\'email:', emailError);
        // On ne lance pas d'erreur ici pour ne pas empêcher l'enregistrement de la signature
      }
    } catch (emailError) {
      console.error('[DEBUG saveCharterSignatory] Erreur lors de l\'envoi de l\'email de vérification:', emailError);
      // On ne lance pas d'erreur ici pour ne pas empêcher l'enregistrement de la signature
    }
    
    return {
      id: signatoryId,
      verification_token: verificationToken
    };
  } catch (error) {
    console.error('[DEBUG saveCharterSignatory] Erreur attrapée:', error);
    throw error;
  }
}
